// =============================================================================
// VibeSelectorFull — rich vibe cards for the Settings → Voice page
// =============================================================================
// Each vibe gets a card with label + tagline + description + sample greetings.
// Active vibe shows an "ACTIVE" pill. Locked vibes are grayed out with the
// persona-specific lock message (no Select button). Custom vibe shows a text
// field for the user's own opening line.
//
// All changes are persisted by the caller (Settings) via PATCH /api/settings —
// this component is purely controlled.
// =============================================================================

import { Lock } from 'lucide-react'
import { cn } from '@/lib/cn'
import {
  VIBE_DEFS,
  isAllowed,
  lockMessage,
  type Vibe,
  type VibePersona,
} from '@/lib/vibes'

const VIBE_ORDER: Vibe[] = ['professional', 'casual', 'direct', 'hype', 'custom']

interface VibeSelectorFullProps {
  currentVibe: Vibe
  personaId: VibePersona
  onChange: (vibe: Vibe) => void
  customGreeting: string
  onCustomGreetingChange: (value: string) => void
}

export function VibeSelectorFull({
  currentVibe,
  personaId,
  onChange,
  customGreeting,
  onCustomGreetingChange,
}: VibeSelectorFullProps) {
  return (
    <div className="space-y-2">
      {VIBE_ORDER.map((vibe) => {
        const def = VIBE_DEFS[vibe]
        const allowed = isAllowed(personaId, vibe)
        const locked = !allowed
        const active = currentVibe === vibe && allowed
        const lock = locked
          ? lockMessage(personaId, vibe) ?? 'Not available for this persona'
          : null

        return (
          <button
            key={vibe}
            type="button"
            onClick={() => {
              if (!locked) onChange(vibe)
            }}
            disabled={locked}
            aria-pressed={active}
            className={cn(
              'w-full text-left rounded-lg border px-4 py-3 transition-colors',
              active
                ? 'border-jarvis-blue/60 bg-jarvis-blue/5'
                : 'border-jarvis-border bg-jarvis-surface hover:border-jarvis-blue/30',
              locked && 'opacity-50 cursor-not-allowed hover:border-jarvis-border',
            )}
          >
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                {/* Status dot — solid blue when active, hollow when not, lock when blocked */}
                {locked ? (
                  <Lock size={12} className="text-jarvis-muted" aria-hidden />
                ) : (
                  <span
                    className={cn(
                      'inline-block w-2.5 h-2.5 rounded-full border-2 transition-colors',
                      active
                        ? 'bg-jarvis-blue border-jarvis-blue'
                        : 'border-jarvis-muted',
                    )}
                    aria-hidden
                  />
                )}
                <span className="text-sm font-medium">{def.label}</span>
                <span className="text-xs text-jarvis-muted italic">
                  {def.tagline}
                </span>
              </div>
              {active && !locked && (
                <span className="text-xs uppercase tracking-widest text-jarvis-blue">
                  Active
                </span>
              )}
            </div>

            {locked && lock && (
              <p className="text-xs text-jarvis-muted mt-2">{lock}</p>
            )}

            {vibe === 'custom' && active && !locked && (
              <div className="mt-3" onClick={(e) => e.stopPropagation()}>
                <input
                  type="text"
                  value={customGreeting}
                  onChange={(e) => onCustomGreetingChange(e.target.value)}
                  placeholder="Your opening line"
                  className="w-full bg-jarvis-surface-2 border border-jarvis-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-jarvis-blue/60"
                />
              </div>
            )}
          </button>
        )
      })}
    </div>
  )
}
